import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PostPosition } from "@actions/futures";
import { getWallets } from "@actions/wallet";
import { getTodayStatistics } from "@actions";
import { useTranslation } from "@helpers/translate";
import { ALERT } from "../../../../../../../constants";
import { MIN_ORDER_COST_USDT } from "../constants";

/**
 * Validation + market place-trade flow previously inline in Controls.
 *
 * @param {object} params
 * @param {number} params.currentPrice
 * @param {object} params.pair
 * @param {number} params.availableBalance
 * @param {string} params.cost
 * @param {number} params.numericCost
 * @param {number} params.percentage
 * @param {number} params.leverage
 * @param {number|null} params.takeProfitPercent
 * @param {number|null} params.stopLossPercent
 * @param {string} params.direction
 * @param {() => void} [params.onSuccess]
 */
export const usePlaceTrade = ({
  currentPrice,
  pair,
  availableBalance,
  cost,
  numericCost,
  percentage,
  leverage,
  takeProfitPercent,
  stopLossPercent,
  direction,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const { __ } = useTranslation();
  const [isPlacingTrade, setIsPlacingTrade] = useState(false);
  // The mark price tick shown on the page: the server opens the position at its own price of that tick
  const { markPrice, markPriceTickAt } = useSelector((state) => state.futures);

  const getCostValidationError = () => {
    const trimmedCost = String(cost ?? "").trim();

    if (trimmedCost === "") {
      return __("futures.validation.cost_required");
    }

    if (!Number.isFinite(numericCost) || numericCost < MIN_ORDER_COST_USDT) {
      return __("futures.validation.cost_min").replace(
        "{min}",
        `${MIN_ORDER_COST_USDT} USDT`,
      );
    }

    return null;
  };

  const placeTrade = async (directionArg) => {
    const normalizedDirection =
      String(directionArg || "")
        .trim()
        .toLowerCase() === "short"
        ? "short"
        : "long";

    const costValidationError = getCostValidationError();

    if (costValidationError) {
      dispatch({
        type: ALERT.ALERT_ERROR,
        payload: costValidationError,
      });

      return false;
    }

    if (Number.isFinite(numericCost) && numericCost > availableBalance) {
      return false;
    }

    // No quote request first: the backend takes its own price anyway, and the extra
    // round trip only delayed the fill. entry_price is informational, the server ignores it.
    const data = {
      side: normalizedDirection,
      symbol: pair.slug,
      futures_pair_id: pair.id,
      percentage: percentage,
      leverage: leverage,
      take_profit_percent: takeProfitPercent,
      stop_loss_percent: stopLossPercent,
      entry_price: markPrice ?? currentPrice,
      price_tick_at: markPriceTickAt ?? undefined,
      type: "market",
      margin: numericCost,
    };

    await dispatch(PostPosition(data));
    dispatch(getTodayStatistics());
    dispatch(getWallets());

    if (onSuccess) {
      onSuccess();
    }

    return true;
  };

  const handlePlaceTrade = async () => {
    // Pairs list not loaded yet: the default pair has no backend id
    if (isPlacingTrade || !pair?.id) return false;

    setIsPlacingTrade(true);

    try {
      return await placeTrade(direction);
    } catch (error) {
      // Errors are already shown via ALERT in PostPosition
      return false;
    } finally {
      setIsPlacingTrade(false);
    }
  };

  return {
    isPlacingTrade,
    handlePlaceTrade,
  };
};
